import cn from 'classnames';
import { motion, useReducedMotion } from 'motion/react';
import { useState, useEffect, useRef, useCallback } from 'react';

import DefaultLink from 'components/common/DefaultLink';
import Tabs from 'components/common/Tabs';
import * as Routes from 'helpers/routes';
import ChoppedAndReadyShrimpPenne from 'images/familyHome/chopped-and-ready-shrimp-penne.jpg';
import ExpressSalmonNoodles from 'images/familyHome/express-salmon-noodles.jpg';
import HeatAndEatChickenTeri from 'images/familyHome/heat-and-eat-chicken-teri.jpg';
import MealKitPretzelChicken from 'images/familyHome/meal-kit-pretzel-chicken.jpg';
import OvenReadySteak from 'images/familyHome/oven-ready-steak.jpg';
import { STANDARD } from 'lib/constants';
import { logEvent, events } from 'services/analytics';

const MAX_TAB_MARGIN = 16;

const CategoryTab = ({
  classNames,
  imageSrc,
  imageAlt,
  categoryTitle,
  categoryDescription,
  ctaLabel,
  labels,
}) => {
  return (
    <li
      className={cn(
        'relative z-50 -mt-3 grid w-full grid-cols-1 items-stretch justify-between gap-8 rounded-2xl p-6 pb-12 bp2:grid-cols-[2fr_3fr] bp2:gap-12 bp2:p-10 bp2:pb-10',
        classNames
      )}
    >
      <div className="mx-auto flex max-w-md flex-col bp2:mx-0 bp2:mt-4">
        <h2 className="h1 pb-4">{categoryTitle}</h2>
        <p className="h5">{categoryDescription}</p>
      </div>
      <div className="relative w-full bp2:row-span-2">
        {labels && labels.length > 0 && (
          <div className="invisible absolute left-0 top-0 mt-6 flex w-full flex-row justify-between gap-2 px-6 bp2:visible">
            {labels.map((label, index) => (
              <span key={index} className={cn('textMd rounded-lg px-2 py-1', classNames)}>
                {label}
              </span>
            ))}
          </div>
        )}
        <img
          src={imageSrc}
          alt={imageAlt}
          className="mb-6 !h-48 w-full rounded-xl object-cover bp2:mb-0 bp2:!h-[440px]"
        />
      </div>
      <DefaultLink
        to={Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro}
        isExternal={true}
        variation="Primary"
        className="btn btnLg w-fit place-self-center self-start border-none !bg-white-110 !text-green-140 hover:brightness-90 bp2:place-self-start"
        onClick={() =>
          logEvent(events.familyHome.submitted, {
            urlPath: Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro,
          })
        }
      >
        {ctaLabel}
      </DefaultLink>
    </li>
  );
};

CategoryTab.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  categoryTitle: PropTypes.string.isRequired,
  categoryDescription: PropTypes.string.isRequired,
  ctaLabel: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  classNames: PropTypes.string,
};

const IMAGE_MAP = {
  meal_kits: MealKitPretzelChicken,
  express: ExpressSalmonNoodles,
  oven_ready: OvenReadySteak,
  chopped_and_ready: ChoppedAndReadyShrimpPenne,
  heat_and_eat: HeatAndEatChickenTeri,
};

const COLOR_MAP = {
  meal_kits: 'border-green-140 bg-green-140 text-white-110',
  express: 'border-[#DC4405] !bg-[#DC4405] text-white-110',
  oven_ready: 'border-blue-100 !bg-blue-100 text-white-110',
  chopped_and_ready: 'border-purple-100 bg-purple-100 text-white-110',
  heat_and_eat: 'border-[#FFB046] !bg-[#FFB046] text-green-140',
};

const MealCategories = ({ data: { title, categories } }) => {
  const [activeView, setActiveView] = useState(categories[0].heading);
  const tablistContainerRef = useRef(null);

  const category = categories.map(category => {
    return {
      ...category,
      imageSrc: IMAGE_MAP[category.key],
      categoryTitle: category.heading,
      categoryDescription: category.body,
      imageAlt: category.imageAlt,
      className: COLOR_MAP[category.key],
    };
  });

  const activeIndex = category.findIndex(category => category.heading === activeView);

  const tabs = category.map((category, index) => {
    const distance = Math.abs(index - activeIndex);
    const zIndexClass =
      distance === 0
        ? 'z-50'
        : distance === 1
          ? 'z-40'
          : distance === 2
            ? 'z-30'
            : distance === 3
              ? 'z-20'
              : 'z-10';

    return {
      label: category.heading,
      className: cn(category.className, 'relative', index === activeIndex ? 'z-[60]' : zIndexClass),
    };
  });

  const activeCategory = category.find(category => category.heading === activeView);

  const applyTabMarign = useCallback(() => {
    const tabContainer = tablistContainerRef.current?.querySelector('[role="tablist"]');

    if (!tabContainer) return;

    const tabButtons = Array.from(tabContainer.querySelectorAll('button[role="tab"]'));

    if (!tabButtons.length) return;

    const tabContainerWidth = tabContainer.clientWidth;
    const totalTabWidth = tabButtons.reduce((sum, button) => sum + button.offsetWidth, 0);

    const tabMargin = Math.min(
      MAX_TAB_MARGIN,
      Math.floor((tabContainerWidth - totalTabWidth) / (tabButtons.length - 1))
    );

    tabButtons.forEach((button, index) => {
      button.style.marginLeft = index === 0 ? '0px' : `${tabMargin}px`;
    });
  }, []);

  useEffect(() => {
    applyTabMarign();

    const resizeObserver = new ResizeObserver(applyTabMarign);

    if (tablistContainerRef.current) {
      resizeObserver.observe(tablistContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [applyTabMarign]);

  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="-mt-px flex flex-col justify-center bg-white-110 px-4 py-8 bp2:py-20">
      <motion.h2
        className="h1 mx-auto mb-8 max-w-lg text-center text-green-140 bp1:mb-12"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.5 }}
      >
        {title}
      </motion.h2>
      <motion.div
        ref={tablistContainerRef}
        className="mx-auto w-full max-w-full bp2:max-w-7xl"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <Tabs
          onChange={setActiveView}
          activeView={activeView}
          tabsProps={{
            tabs,
            containerAriaLabel: 'Meal Categories',
            classes: {
              active: cn(activeCategory?.className, 'relative z-50'),
              contentContainer:
                '!rounded-t-2xl textMd bp2:text-[24px] shrink-0 uppercase pt-3 pb-5 bp2:px-6 px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-100 transition-none',
            },
          }}
        />
        {activeCategory && (
          <CategoryTab {...activeCategory} classNames={activeCategory?.className} />
        )}
      </motion.div>
    </section>
  );
};

MealCategories.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        heading: PropTypes.string.isRequired,
        body: PropTypes.string.isRequired,
        imageAlt: PropTypes.string.isRequired,
        ctaLabel: PropTypes.string.isRequired,
        labels: PropTypes.arrayOf(PropTypes.string),
      })
    ).isRequired,
  }).isRequired,
};

export default MealCategories;
